import React, { FC } from "react";

import { BaseComponentProps } from "@keepeek/commons";
import { Link as MUILink, Typography } from "@mui/material";
import clsx from "clsx";
import Link from "next/link";
import { useRouter } from "next/router";

import { KIcon } from "../../../../components/common/KIcon";
import {
  LinkByLocale,
  LinkType,
  LocaleLinkWithIconSchema,
} from "../../../../models/configuration/components/kHeaderWidget";

export type CustomLinkProps = {
  localeLinkWithIconSchema: LocaleLinkWithIconSchema;
  color: string | undefined;
} & BaseComponentProps;

/**
 * Represent a i18n link with an option icon on the left or on the right
 */
const CustomLink: FC<React.PropsWithChildren<CustomLinkProps>> = function CustomLink({
  localeLinkWithIconSchema: {
    iconKey,
    linkByLocale,
    iconPosition,
    marginLeft,
    marginRight,
    openInNewTab,
  },
  color,
  className,
}) {
  const { locale } = useRouter();

  const link: LinkByLocale | undefined = linkByLocale?.find((l) => l.locale === locale);
  const position = iconPosition?.toString().toLocaleUpperCase() || "LEFT";

  const customLinkStyle: React.CSSProperties = {
    marginLeft: marginLeft !== undefined ? `${marginLeft}rem` : "0",
    marginRight: marginRight !== undefined ? `${marginRight}rem` : "0",
  };

  /**
   * Basic KIcon with icon and color parameterized
   */
  const CustomIcon: FC<
    React.PropsWithChildren<{ iconKey: string | undefined; color: string | undefined }>
  > = function ({ iconKey, color }) {
    if (!iconKey) {
      return null;
    }
    return <KIcon sx={color ? { color: color } : {}} className={iconKey} />;
  };

  // no custom links for this language
  if (!link) {
    return null;
  }

  return (
    <Link
      href={
        link.linkType === LinkType.Didomi ? "javascript:Didomi.preferences.show()" : link.href || ""
      }
      passHref
      legacyBehavior
    >
      <MUILink
        color={color}
        title={link?.title}
        target={openInNewTab ? "_blank" : "_self"}
        className={clsx(className, "CustomLink")}
        style={customLinkStyle}
        rel="noreferrer"
        component="a"
      >
        {position === "LEFT" && <CustomIcon iconKey={iconKey} color={color} />}
        <Typography
          variant="button"
          sx={{
            color: (theme) => (color ? color : theme.palette.common.black),
            textTransform: "none",
          }}
          style={{
            color: color,
            textTransform: "none",
          }}
        >
          {link?.label}
        </Typography>
        {position === "RIGHT" && <CustomIcon iconKey={iconKey} color={color} />}
      </MUILink>
    </Link>
  );
};

export default CustomLink;
